import React, { useState, useEffect } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
} from 'recharts';
import { useLocation } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import BottomBar from '../Components/BottomBar';
import backgroundImage from '../images/bgnew.png';
import { ChartPie, BarChart3, List, Edit2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const colorPalette = ['#2c3e50', '#000000', '#7f8c8d', '#2c3e50', '#7f8c8d', '#000000'];

function VotingStatisticsUpdated() {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const paramElectionId = searchParams.get('electionId') || null;

    const [election, setElection] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [pieData, setPieData] = useState([]);
    const [totalVotes, setTotalVotes] = useState(0);
    const [viewMode, setViewMode] = useState('bar');
    const [scale, setScale] = useState(1);

    const fetchElectionById = async (id) => {
        const res = await axios.get(`https://backend.block-ballot.com/elections/${id}`, {
            validateStatus: () => true,
        });
        if (res.status === 200) {
            return res.data;
        }
        return null;
    };

    const fetchActiveElection = async () => {
        const res = await axios.get("https://backend.block-ballot.com/elections/active", {
            validateStatus: () => true,
        });
        if (res.status === 204) return null;
        if (res.status === 200) return res.data;
        return null;
    };

    const loadElectionData = async (id) => {
        try {
            let fetchedElection = null;
            if (id) {
                fetchedElection = await fetchElectionById(id);
            }
            if (!fetchedElection) {
                fetchedElection = await fetchActiveElection();
            }
            if (!fetchedElection) {
                setElection(null);
                setCandidates([]);
                setTotalVotes(0);
                return;
            }
            setElection(fetchedElection);

            const candidatesRes = await axios.get(
                `https://backend.block-ballot.com/api/election/${fetchedElection.id}/candidates`
            );
            let updatedCandidates = candidatesRes.data || [];

            updatedCandidates = await Promise.all(
                updatedCandidates.map(async (candidate, index) => {
                    const voteRes = await axios.get(
                        `https://backend.block-ballot.com/api/getVotes/${candidate.empId}`
                    );
                    const votes = parseInt(voteRes.data) || 0;
                    return {
                        ...candidate,
                        votes,
                        color: colorPalette[index % colorPalette.length],
                    };
                })
            );

            const total = updatedCandidates.reduce((acc, c) => acc + c.votes, 0);
            setTotalVotes(total);

            updatedCandidates = updatedCandidates.map((c) => ({
                ...c,
                percentage: total > 0 ? ((c.votes / total) * 100).toFixed(2) : 0,
            }));

            updatedCandidates.sort((a, b) => b.votes - a.votes);
            setCandidates(updatedCandidates);

            setChartData(
                updatedCandidates.map((c) => ({
                    name: c.nameOfCandidate,
                    votes: c.votes,
                    color: c.color,
                }))
            );
            setPieData(
                updatedCandidates.map((c) => ({
                    name: c.nameOfCandidate,
                    value: c.votes,
                    color: c.color,
                }))
            );
        } catch (error) {
            console.error("Error loading election data:", error);
        }
    };

    useEffect(() => {
        loadElectionData(paramElectionId);
        // eslint-disable-next-line
    }, [paramElectionId]);

    return (
        <div
            className="h-screen flex flex-col font-[Montserrat] ml-10"
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            <header className="flex items-center justify-center px-4 py-3 md:px-6 md:py-4 bg-[#0a1622]">
                <h1 className="font-bold text-white text-base md:text-lg">Voting Statistics</h1>
            </header>
            <Sidebar />
            <div className="flex-1 transition-all duration-300 p-2 md:p-5 bg-gray-10 pl-4 md:pl-64">
                <h2 className="text-xl md:text-2xl font-bold text-black text-center md:text-left mt-2">
                    Voting Statistics {election ? `for ${election.name}` : ""}
                </h2>

                {election ? (
                    <>
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                            {/* Total Votes */}
                            <div className="p-4 bg-white rounded shadow-md text-center">
                                <h3 className="text-lg font-bold text-black">Total Votes</h3>
                                <p className="text-3xl font-bold mt-2 text-black">{totalVotes}</p>
                            </div>

                            {/* Top 1 Candidate */}
                            {candidates[0] && (
                                <div className="p-4 bg-white rounded shadow-md text-center">
                                    <h3 className="text-lg font-bold text-black">
                                        Top 1: {candidates[0].nameOfCandidate}
                                    </h3>
                                    <p className="text-3xl font-bold mt-2 text-black">
                                        {candidates[0].votes} votes
                                    </p>
                                    <p className="text-green-600 text-sm font-bold">
                                        {candidates[0].percentage}% of total
                                    </p>
                                </div>
                            )}

                            {/* Top 2 Candidate */}
                            {candidates[1] && (
                                <div className="p-4 bg-white rounded shadow-md text-center">
                                    <h3 className="text-lg font-bold text-black">
                                        Top 2: {candidates[1].nameOfCandidate}
                                    </h3>
                                    <p className="text-3xl font-bold mt-2 text-black">
                                        {candidates[1].votes} votes
                                    </p>
                                    <p className="text-green-600 text-sm font-bold">
                                        {candidates[1].percentage}% of total
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Main Chart/List Container */}
                        <div
                            className="mt-4 bg-white p-3 rounded shadow-md flex flex-col h-[calc(89vh-10rem)] relative">
                            {/* Edit Election Button */}
                            <div className="absolute top-2 right-2">
                                <Link to="/RegisterElectionPage">
                                    <button className="p-2 rounded-full hover:bg-gray-300">
                                        <Edit2 size={20} className="text-black" />
                                    </button>
                                </Link>
                            </div>

                            {/* Toggle Button */}
                            <div className="flex justify-between items-center mb-2">
                                <button
                                    className="px-3 py-1 rounded flex items-center gap-2 hover:bg-gray-300"
                                    onClick={() =>
                                        setViewMode(
                                            viewMode === 'bar'
                                                ? 'pie'
                                                : viewMode === 'pie'
                                                    ? 'list'
                                                    : 'bar'
                                        )
                                    }
                                >
                                    {viewMode === 'bar' ? (
                                        <ChartPie size={20} />
                                    ) : viewMode === 'pie' ? (
                                        <List size={20} />
                                    ) : (
                                        <BarChart3 size={20} />
                                    )}
                                </button>
                            </div>

                            <div
                                className="flex-1 overflow-auto"
                                style={{
                                    transform: `scale(${scale})`,
                                    transformOrigin: 'top left',
                                }}
                            >
                                {viewMode === 'bar' && (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="votes">
                                                {chartData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={entry.color}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}

                                {viewMode === 'pie' && (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={140}
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={entry.color}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}

                                {viewMode === 'list' && (
                                    <div className="w-full max-w-3xl mx-auto space-y-3 p-2">
                                        {candidates.map((candidate, index) => (
                                            <div
                                                key={index}
                                                className="bg-white p-3 rounded shadow-md"
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-semibold text-gray-700">
                                                        {candidate.nameOfCandidate}
                                                    </span>
                                                    <span className="text-gray-700">
                                                        {candidate.percentage}%
                                                    </span>
                                                </div>
                                                <div className="relative w-full h-4 bg-gray-200 rounded">
                                                    <div
                                                        className="absolute left-0 top-0 h-4 rounded"
                                                        style={{
                                                            width: `${candidate.percentage}%`,
                                                            backgroundColor: candidate.color,
                                                        }}
                                                    />
                                                </div>
                                                <div className="text-right mt-2 text-sm text-gray-600">
                                                    {candidate.votes} votes
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center text-black mt-10">
                        <h3>No active or selected election found.</h3>
                    </div>
                )}
            </div>
            <BottomBar />
        </div>
    );
}

export default VotingStatisticsUpdated;
